import { Box, Button, styled, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { Suggestion } from 'Types/prompt';

import { useTestId } from '../../../hooks/useTestId';

interface StepBuilderInputSectionProps {
  stepLabel: string;
  idx: number;
  stepType: string | undefined;
  suggestions: Suggestion[] | undefined;
  sectionRef?: React.Ref<HTMLDivElement>;
  onChange: (text: string) => void;
}

const ChipsContainer = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  border: '1px solid',
  borderColor: '#D0D5DD',
  borderRadius: '10px',
  padding: '8px',
  marginBottom: '16px',
}));

const ChipButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected?: boolean }>(({ isSelected }) => ({
  borderRadius: '50px',
  textTransform: 'none',
  borderColor: '#070154',
  gap: '6px',
  backgroundColor: isSelected ? '#070154' : 'transparent',
  color: isSelected ? '#fff' : '#070154',
  '&:hover': {
    backgroundColor: isSelected ? '#070154' : '#f0f0f0',
  },
}));

const StepBuilderInputSection: React.FC<StepBuilderInputSectionProps> = ({
  stepLabel,
  idx,
  stepType,
  suggestions = [],
  sectionRef,
  onChange,
}) => {
  const tid = useTestId('prompt-step-input-section');
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [manualInput, setManualInput] = useState('');
  const textValue = selectedSuggestion?.description ?? manualInput;
  const prefix = `${stepType?.charAt(0).toUpperCase()}${stepType?.slice(1).toLowerCase()}: `;

  const handleSuggestionClick = useCallback(
    (suggestion: Suggestion, isSelected: boolean) => {
      if (isSelected) {
        setSelectedSuggestion(null);
        onChange('');
      } else {
        setSelectedSuggestion(suggestion);
        onChange(`${prefix}${suggestion.description}`);
        setManualInput('');
      }
    },
    [onChange, prefix]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setManualInput(value);

      if (selectedSuggestion) {
        setSelectedSuggestion(null);
      }

      onChange?.(`${prefix}${value}`);
    },
    [onChange, selectedSuggestion, prefix]
  );

  return (
    <Box ref={sectionRef} sx={{ pb: 4 }}>
      <Typography variant="h5" color="#0047FF" gutterBottom>
        {stepLabel}
      </Typography>

      <Typography variant="body1" sx={{ color: 'text.secondary', pb: 1, pt: 1 }}>
        SUGGESTIONS
      </Typography>

      <ChipsContainer>
        {suggestions.map((suggestion) => {
          const isSelected = selectedSuggestion === suggestion;
          return (
            <ChipButton
              key={`${suggestion.title}_${idx}`}
              variant={isSelected ? 'contained' : 'outlined'}
              size="small"
              isSelected={isSelected}
              aria-pressed={isSelected}
              onClick={() => handleSuggestionClick(suggestion, isSelected)}
              data-testid={tid(`suggestion-${suggestion.step}`)}
            >
              {suggestion.title}
            </ChipButton>
          );
        })}
      </ChipsContainer>

      <TextField
        fullWidth
        multiline
        minRows={5}
        placeholder="Use a suggestion or enter your own"
        variant="outlined"
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
          },
        }}
        inputProps={{
          'aria-label': `step-${stepType}`,
        }}
        value={textValue}
        onChange={handleTextChange}
        data-testid={tid(`${stepType}`)}
      />
    </Box>
  );
};

export default StepBuilderInputSection;
